import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FiCpu, FiSun, FiMoon, FiSunrise } from 'react-icons/fi';
import '../styles/aigreeting.css';

const AIGreeting = () => {
  const location = useLocation();
  const currentPath = (location.pathname || '').toLowerCase().replace(/\/$/, '');
  const isDashboard = currentPath === '/dashboard';

  const [greeting, setGreeting] = useState('');
  const [userName, setUserName] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAutoOpen, setIsAutoOpen] = useState(false);
  const [timeIcon, setTimeIcon] = useState(<FiSun />);
  const messageRef = useRef(null);

  const messages = [
    "Hamara Bill Book keeps your business data 100% secure.",
    "Did you know? You can generate customized PDF invoices in seconds.",
    "Hamara Bill Book helps you track your inventory effortlessly.",
    "You can share invoices directly via WhatsApp to your clients.",
    "Check out the Reports tab to see auto-generated financial insights.",
    "Hamara Bill Book is your all-in-one GST compliance partner.",
    "Use the thermal print format for quick receipt printing at the counter!",
    "Need to manage your store collaboratively? Add multiple staff accounts easily."
  ];

  // Get user name and set greeting
  useEffect(() => {
    if (!isDashboard) return;

    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        if (userObj && userObj.name) {
          setUserName(userObj.name);
        }
      }
    } catch (e) {
      console.error(e);
    }

    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
      setTimeIcon(<FiSunrise />);
    } else if (hour < 18) {
      setGreeting('Good Afternoon');
      setTimeIcon(<FiSun />);
    } else {
      setGreeting('Good Evening');
      setTimeIcon(<FiMoon />);
    }
  }, [isDashboard]);

  // Auto-open logic (every 2.5 minutes for 10 seconds) - ONLY on dashboard
  useEffect(() => {
    if (!isDashboard) {
      setIsAutoOpen(false);
      return;
    }
    
    // We don't do the Welcome Back here anymore, it's handled by WelcomeSplash
    // Just start the normal interval and maybe pop open once quietly
    
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
      setIsAutoOpen(true);
      setTimeout(() => setIsAutoOpen(false), 10000);
    }, 150000); // 2.5 minutes

    return () => clearInterval(interval);
  }, [isDashboard, messages.length]);

  // Message rotation on hover
  useEffect(() => {
    if (!isDashboard) return;
    let interval;
    if (isHovered) {
      interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [isDashboard, isHovered, messages.length]);

  // Typewriter effect using Direct DOM for 60FPS flawless typing (No React Glitch)
  useEffect(() => {
    if (!isDashboard) return;
    const active = isHovered || isAutoOpen;
    if (!active) {
      if (messageRef.current) messageRef.current.innerHTML = '';
      return;
    }

    const fullText = messages[messageIndex];
    let i = 0;
    
    if (messageRef.current) {
      messageRef.current.innerHTML = '<span class="cursor-blink">|</span>';
    }
    
    const typingInterval = setInterval(() => {
      if (i < fullText.length) {
        if (messageRef.current) {
          messageRef.current.innerHTML = fullText.slice(0, i + 1) + '<span class="cursor-blink">|</span>';
        }
        i++;
      } else {
        clearInterval(typingInterval);
      }
    }, 30); // 30ms typing speed for snappy feel

    return () => clearInterval(typingInterval);
  }, [isDashboard, messageIndex, isHovered, isAutoOpen, messages]);

  // Completely hide AI greeting on any screen that is not the dashboard
  if (!isDashboard) {
    return null;
  }

  const showContent = isHovered || isAutoOpen;
  const finalGreeting = userName ? `${greeting}, ${userName}!` : `${greeting}!`;

  return (
    <div 
      className={`ai-greeting-container ${showContent ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="ai-greeting-content">
        <div className="ai-greeting-header">
          <span className="ai-greeting-icon">{timeIcon}</span>
          <h4>{finalGreeting}</h4>
        </div>
        <p className="ai-greeting-message" ref={messageRef}>
        </p>
      </div>
      <div className="ai-avatar-button">
        <FiCpu className="ai-bot-icon" />
        <span className="ai-pulse-ring"></span>
      </div>
    </div>
  );
};

export default AIGreeting;
